import React, { useMemo } from 'react';
import classNames from 'classnames';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import { ButtonWrapper, DynamicMediaItem } from '../../../../components';
import type { BlockCTA } from '../../../../types/uncinc_profile/blocks';

import './style.scss';

export interface CtaBlockProps {
  entity: ImmutableEntity<BlockCTA>;
  leftAlign: boolean,
  additonalLinkParams?: string,
}

const CTABlock: React.FC<CtaBlockProps> = ({ entity, leftAlign = false, additonalLinkParams = '' }) => {
  const ctaBlock = entity.toJS();
  const title = ctaBlock.info[0].value;
  const body = ctaBlock.body[0]?.processed;
  const image = ctaBlock.field_media[0];
  const imageAsBackground = ctaBlock.field_use_as_background[0]?.value === true;
  const linkTitle = ctaBlock.field_link[0].title;
  const linkUri = `${ctaBlock.field_link[0].alias || ctaBlock.field_link[0].uri}${additonalLinkParams}`;
  const secondaryLinkTitle = ctaBlock.field_link_secondary[0]?.title;
  const secondaryLinkUri = ctaBlock.field_link_secondary[0]?.alias
    || ctaBlock.field_link_secondary[0]?.uri;
  const classes = classNames('Block', 'CTABlock');

  const contentColClasses = useMemo(() => {
    if (!imageAsBackground && image) {
      return 'col-12 col-md-8 col-lg-7';
    }

    return leftAlign
      ? 'col-12 left-align'
      : 'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2';
  }, [image, imageAsBackground, leftAlign]);

  return (
    <div className={classes}>
      <Container>
        <div className="row">
          {(!imageAsBackground && image) && (
            <div className="col-12 col-md-4 col-lg-3 offset-lg-1">
              <div className="CTABlock__image-wrapper">
                <DynamicMediaItem
                  className="CTABlock__image"
                  media={image}
                  imageAspectRatio="1_1"
                />
              </div>
            </div>
          )}

          <div className={contentColClasses}>
            <div className="CTABlock__wrapper">
              {(imageAsBackground && image) && (
                <div className="CTABlock__overlay">
                  <DynamicMediaItem
                    media={image}
                    imageAspectRatio="3_2"
                    className="CTABlock__background-image"
                  />
                </div>
              )}

              <div className="CTABlock__content">
                <h2 className="CTABlock__title">
                  {title}
                </h2>

                {body && (
                  <WysiwygField
                    className="CTABlock__body"
                    property="schema:text"
                    html={body}
                  />
                )}
              </div>

              <div className="CTABlock__links">
                <ButtonWrapper inverted href={linkUri}>
                  {linkTitle}
                </ButtonWrapper>

                {secondaryLinkUri && (
                  <ButtonWrapper type="secondary" inverted href={secondaryLinkUri}>
                    {secondaryLinkTitle}
                  </ButtonWrapper>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default CTABlock;
